/**
 * Fetches which sportsbooks currently have odds coverage for a league/market, so the picker
 * can be limited to books that actually have data. Fail-safe: returns null (meaning "no
 * limiting, show the full list") while loading or on any error, so the picker never breaks.
 */

import { API_BASE_URL } from '@config/api';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { useEffect, useState } from 'react';

export type CoverageMarket = 'props' | 'game_lines';

export function useSportsbookCoverage(
  leagueKey: LeagueKey | undefined,
  market: CoverageMarket = 'props',
): string[] | null {
  const [covered, setCovered] = useState<string[] | null>(null);

  useEffect(() => {
    if (!leagueKey) {
      setCovered(null);
      return;
    }
    let cancelled = false;
    const params = new URLSearchParams({ league: leagueKey, market });
    fetch(`${API_BASE_URL}/api/sportsbooks/coverage?${params.toString()}`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: { sportsbooks?: string[] }) => {
        if (!cancelled) setCovered(Array.isArray(data.sportsbooks) ? data.sportsbooks : null);
      })
      .catch(() => {
        if (!cancelled) setCovered(null);
      });
    return () => {
      cancelled = true;
    };
  }, [leagueKey, market]);

  return covered;
}
